// libs/shared/ui/builder/src/lib/components/auth/AgentAuthSignIn.tsx
import React, { useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

export type AuthView =
  | "signin"
  | "forgot-password"
  | "forgot-agent-id"
  | "register";

interface Props {
  authServiceUrl: string;
  agentPortalUrl: string;
  onNavigate: (view: AuthView) => void;
}

const roundedInputSx = {
  "& .MuiOutlinedInput-root": { borderRadius: "5px" },
  "& .MuiInputBase-input": { padding: "8px 14px" },
};

export default function AgentAuthSignIn({
  authServiceUrl,
  agentPortalUrl,
  onNavigate,
}: Props) {
  const [agentId, setAgentId] = useState("");
  const [password, setPassword] = useState("");
  const [agentIdError, setAgentIdError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");

  function validate(): boolean {
    let valid = true;
    if (!agentId.trim()) {
      setAgentIdError("Agent ID is required");
      valid = false;
    } else {
      setAgentIdError("");
    }
    if (!password.trim()) {
      setPasswordError("Password is required");
      valid = false;
    } else {
      setPasswordError("");
    }
    return valid;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setApiError("");
    try {
      const res = await fetch(
        `${authServiceUrl}/service/users/login?role=AGENT`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId: agentId.trim(), password }),
        }
      );
      if (res.status !== 202) {
        setApiError("Invalid agent ID or password.");
        return;
      }
      const token = res.headers.get("Authorization");
      if (!token) {
        setApiError("Authentication failed. Please try again.");
        return;
      }

      // Parse profile from response body
      const user = (await res.json()) as {
        id?: string;
        firstName?: string;
        lastName?: string;
      };

      // Decode roles from JWT payload (same approach as agent-portal's auth router)
      let roles: string[] = [];
      try {
        const base64Payload = (token.split(".")[1] ?? "")
          .replace(/-/g, "+")
          .replace(/_/g, "/");
        const claims = JSON.parse(atob(base64Payload)) as {
          roles?: Array<{ name: string }>;
        };
        roles = (claims.roles ?? []).map((r) => r.name);
      } catch {
        // Non-standard token — proceed with empty roles
      }

      // Pass auth data to agent-portal via URL — localStorage is not shared
      // across domains (www.pivothealth.com → agents.pivothealth.com).
      // Agent-portal reads this param on mount, hydrates state, then cleans the URL.
      const handoff = encodeURIComponent(
        JSON.stringify({
          token,
          agentId: user.id ?? agentId.trim(),
          fullName: [user.firstName, user.lastName].filter(Boolean).join(" "),
          roles,
        })
      );
      const separator = agentPortalUrl.includes("?") ? "&" : "?";
      window.location.href = `${agentPortalUrl}${separator}handoff=${handoff}`;
    } catch {
      setApiError(
        "Unable to sign in. Please check your connection and try again."
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Stack
      spacing={2}
      maxWidth={400}
      width="100%"
      component="form"
      onSubmit={handleSubmit}
      noValidate
      aria-labelledby="signin-heading"
      aria-busy={submitting}
    >
      {/* Pre-rendered live region — always in DOM; children toggled */}
      <div role="alert" aria-live="assertive" aria-atomic="true">
        {apiError && <Alert severity="error">{apiError}</Alert>}
      </div>

      <div>
        <Typography
          id="signin-heading"
          variant="h5"
          fontWeight={700}
          component="h1"
        >
          Agent Sign In
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          Enter your agent ID and password to sign in.
        </Typography>
      </div>

      <Box>
        <Typography
          component="label"
          htmlFor="signin-agent-id"
          variant="body2"
          sx={{ display: "block", mb: 0.5 }}
        >
          Agent Id
        </Typography>
        <TextField
          inputProps={{ id: "signin-agent-id" }}
          placeholder="Enter your agent id"
          value={agentId}
          onChange={(e) => setAgentId(e.target.value)}
          error={!!agentIdError}
          helperText={agentIdError}
          autoComplete="username"
          fullWidth
          sx={roundedInputSx}
        />
      </Box>

      <Box>
        <Typography
          component="label"
          htmlFor="signin-password"
          variant="body2"
          sx={{ display: "block", mb: 0.5 }}
        >
          Password
        </Typography>
        <TextField
          inputProps={{ id: "signin-password" }}
          type="password"
          placeholder="Enter your password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={!!passwordError}
          helperText={passwordError}
          autoComplete="current-password"
          fullWidth
          sx={roundedInputSx}
        />
      </Box>

      <Stack spacing={0.5} alignItems="center">
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={() => onNavigate("forgot-password")}
        >
          Forgot Password?
        </Link>
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={() => onNavigate("forgot-agent-id")}
        >
          Forgot Agent ID?
        </Link>
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={() => onNavigate("register")}
        >
          Register your agent
        </Link>
      </Stack>

      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={submitting}
        sx={(theme) => ({
          borderRadius: "5px",
          py: "6px",
          fontSize: "0.875rem",
          backgroundColor: theme.palette.primary.main,
          "&:hover": { backgroundColor: theme.palette.primary.light },
        })}
        startIcon={
          submitting ? (
            <CircularProgress size={14} color="inherit" aria-hidden="true" />
          ) : undefined
        }
      >
        {submitting ? "Signing in…" : "Sign in"}
      </Button>
    </Stack>
  );
}
